import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, TextArea } from 'common/components';
import { BotMessageSquare } from 'lucide-react';
import CloudWatchLogger from 'services/cloudwatch/cloudwatchLogger';
import { toast } from 'react-toastify';
import { getBasePath } from 'utils/getBasePath';

import styles from './ErrorBoundary.module.scss';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
    showFeedbackForm: boolean;
    userFeedback: string;
}

class ErrorBoundary extends Component<Props, State> {
    private logger: CloudWatchLogger | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            showFeedbackForm: false,
            userFeedback: '',
        };

        // Initialize logger for error boundary
        this.logger = CloudWatchLogger.getInstance();
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error to CloudWatch with detailed information
        this.logger?.error(
            'React Error Boundary caught an error',
            error,
            {
                error: {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                    cause: (error as any).cause,
                },
                errorInfo: {
                    componentStack: errorInfo.componentStack,
                    errorBoundary: this.constructor.name,
                },
                errorLocation: this.getErrorLocation(),
                context: {
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString(),
                    userId: localStorage.getItem('userId') || 'unknown',
                    userRole: localStorage.getItem('userRole') || 'unknown',
                },
            },
            'ErrorBoundary',
            'error_caught'
        );

        // Also log to console for development
        console.error('ErrorBoundary caught an error:', {
            error: {
                name: error.name,
                message: error.message,
                stack: error.stack,
            },
            errorInfo: {
                componentStack: errorInfo.componentStack,
            },
        });

        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // Update state with error info
        this.setState({
            error,
            errorInfo,
        });
    }

    private handleReload = () => {
        this.logger?.info('User triggered page reload after error', {}, 'ErrorBoundary', 'reload');
        window.location.reload();
    };

    private handleGoHome = () => {
        this.logger?.info('User navigated to home after error', {}, 'ErrorBoundary', 'navigate_home');
        window.location.href = `${getBasePath()}/dashboard`;
    };

    private getErrorLocation = (): string => {
        if (!this.state.error?.stack) {
            return 'No stack trace available';
        }

        const stackLines = this.state.error.stack.split('\n');
        const relevantLines = stackLines
            .filter(
                (line) =>
                    line.includes('.js:') ||
                    line.includes('.tsx:') ||
                    line.includes('.ts:') ||
                    line.includes('ErrorTriggerComponent') ||
                    line.includes('Dashboard') ||
                    line.includes('App')
            )
            .slice(0, 5) // Show first 5 relevant lines
            .map((line) => line.trim());

        return relevantLines.length > 0 ? relevantLines.join('\n') : 'No relevant location information found';
    };

    private handleReportError = () => {
        this.setState({ showFeedbackForm: true });
    };

    private handleFeedbackSubmit = () => {
        if (!this.state.error) return;

        this.logger?.info(
            'User reported error with feedback',
            {
                error: {
                    name: this.state.error.name,
                    message: this.state.error.message,
                    stack: this.state.error.stack,
                    cause: (this.state.error as any).cause,
                },
                errorInfo: {
                    componentStack: this.state.errorInfo?.componentStack,
                },
                errorLocation: this.getErrorLocation(),
                userFeedback: this.state.userFeedback || 'No additional feedback provided',
                context: {
                    url: window.location.href,
                    timestamp: new Date().toISOString(),
                    userId: localStorage.getItem('userId') || 'unknown',
                    userRole: localStorage.getItem('userRole') || 'unknown',
                },
            },
            'ErrorBoundary',
            'error_reported_with_feedback'
        );

        this.setState({
            showFeedbackForm: false,
            userFeedback: '',
        });
        toast.success('Error reported successfully. Thank you for your feedback!');
    };

    private handleFeedbackCancel = () => {
        this.setState({
            showFeedbackForm: false,
            userFeedback: '',
        });
    };

    private handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ userFeedback: e.target.value });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div className={styles.container}>
                    <div className={styles.errorBox}>
                        <div className={styles.iconContainer}>
                            <BotMessageSquare className={styles.errorIcon} />
                        </div>
                        <h1 className={styles.title}>
                            Oops! <br /> Something went wrong
                        </h1>
                        <p className={styles.message}>
                            We're sorry, but something unexpected happened. Our technical team has been automatically
                            notified and is working to fix this issue.
                        </p>
                        <p className={styles.message}>
                            You can try reloading the page or going back to the dashboard. If the problem persists,
                            please contact support.
                        </p>

                        <p className={styles.message}>
                            To report this issue, please{' '}
                            <Button variant='link' onClick={this.handleReportError}>
                                click here
                            </Button>
                        </p>

                        {this.state.showFeedbackForm ? (
                            <div className={styles.feedbackForm}>
                                <h3 className={styles.feedbackTitle}>Report This Issue</h3>
                                <p className={styles.feedbackDescription}>
                                    Please describe what you were doing when this error occurred. This will help us fix
                                    the issue faster.
                                </p>
                                <TextArea
                                    value={this.state.userFeedback}
                                    onChange={this.handleFeedbackChange}
                                    placeholder='Describe what you were doing when the error occurred...'
                                    rows={4}
                                />
                                <div className={styles.feedbackButtons}>
                                    <Button onClick={this.handleFeedbackSubmit} variant='primary'>
                                        Submit Report
                                    </Button>
                                    <Button onClick={this.handleFeedbackCancel} variant='secondary'>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.buttonContainer}>
                                <Button onClick={this.handleReload} variant='primary'>
                                    Reload Page
                                </Button>
                                <Button onClick={this.handleGoHome} variant='secondary'>
                                    Go to Dashboard
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
